import { MotionIcon } from 'motion-icons-react';
import Card from '../../../components/base/Card';
import useScrollReveal from '@/hooks/useScrollReveal';

/**
 * ServerInfoSection component displaying server features and connection information.
 *
 * @description Shows a comprehensive overview of server features including custom content,
 * server details, connection information, and action buttons. Displays live server
 * statistics alongside detailed feature cards.
 *
 * @returns {JSX.Element} The server info section component
 */
export default function ServerInfoSection() {
  const sectionRef = useScrollReveal();
  /**
   * Array of server features with icons, titles, and descriptions.
   *
   * @type {Array<{icon: string, title: string, description: string, animation: string}>}
   */
  const features = [
    {
      icon: 'Swords',
      title: 'Custom Weapons',
      description: 'Unique arsenal with custom weapons not found in vanilla Killing Floor',
      animation: 'shake'
    },
    {
      icon: 'Map',
      title: 'Custom Maps',
      description: 'Exclusive maps designed for enhanced gameplay and new challenges',
      animation: 'bounce'
    },
    {
      icon: 'Skull',
      title: 'Custom Monsters',
      description: 'Face new and terrifying creatures with unique abilities and behaviors',
      animation: 'shake'
    },
    {
      icon: 'Star',
      title: 'Active Admins',
      description: 'Dedicated administrators ensuring fair play and server stability',
      animation: 'spin'
    },
    {
      icon: 'CalendarDays',
      title: 'Regular Events',
      description: 'Weekly tournaments, special events, and community challenges',
      animation: 'bounce'
    },
    {
      icon: 'RefreshCw',
      title: 'Regular Updates',
      description: 'Continuous improvements and new content additions',
      animation: 'spin'
    }
  ];

  return (
    <section id="server-info" ref={sectionRef} className="pt-20 pb-8 bg-gray-900/80">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 scroll-reveal">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-orbitron horror-heading">
            Server <span className="text-red-500">Features</span>
          </h2>
          <div className="w-24 h-1 bg-red-600 mx-auto mb-6"></div>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Experience Killing Floor like never before with our extensive custom content and active community
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="scroll-reveal" style={{ animationDelay: `${(index + 1) * 0.1}s` }}>
              <Card variant="dark" className="text-center hover:scale-[1.02] transition-transform duration-300 h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MotionIcon name={feature.icon} color="white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-orbitron">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </Card>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}
